import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { FormInput, SearchForm } from '../components/FormControls.jsx';
import { Modal, ModalConfirmFooter } from '../components/Modal.jsx';
import { clientLabel } from '../utils/clientLabel.js';

export function AdminClientsPage() {
  const [searchParams] = useSearchParams();
  /** @type {[Array<{ id: number, name: string, email?: string, projects_count?: number }>, Function]} */
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  /** @type {[{ id: number, name: string, projects_count?: number } | null, Function]} */
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [notice, setNotice] = useState('');

  const resetCreateForm = () => {
    setName('');
    setCompanyName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setFieldErrors(undefined);
  };

  const closeCreate = () => {
    setShowCreate(false);
    resetCreateForm();
  };

  const load = () => {
    const params = new URLSearchParams();
    if (search) {
      params.set('search', search);
    }
    return apiJson(`/api/admin/clients?${params}`)
      .then(setClients)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const fromUrl = searchParams.get('search');
    if (fromUrl != null) {
      setSearch(fromUrl);
    }
    if (searchParams.get('create') === '1') {
      setShowCreate(true);
    }
    setLoading(true);
    const params = new URLSearchParams();
    const term = fromUrl || '';
    if (term) {
      params.set('search', term);
    }
    apiJson(`/api/admin/clients?${params}`)
      .then(setClients)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [searchParams]);

  const onCreate = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors(undefined);
    setSubmitting(true);
    try {
      await apiJson('/api/admin/clients', {
        method: 'POST',
        body: {
          name,
          company_name: companyName || null,
          email: email || null,
          phone: phone || null,
          address: address || null,
        },
      });
      closeCreate();
      setLoading(true);
      await load();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    } finally {
      setSubmitting(false);
    }
  };

  const onInvite = async (client) => {
    setError('');
    setNotice('');
    setInvitingId(client.id);
    try {
      await apiJson(`/api/admin/clients/${client.id}/invite`, { method: 'POST' });
      setNotice(`Account created. A password email was sent to ${client.email}.`);
      setLoading(true);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setInvitingId(null);
    }
  };

  const onDelete = async () => {
    if (!clientToDelete) {
      return;
    }
    setError('');
    setDeleting(true);
    try {
      await apiJson(`/api/admin/clients/${clientToDelete.id}`, { method: 'DELETE' });
      setClientToDelete(null);
      setLoading(true);
      await load();
    } catch (err) {
      setError(err.message);
      setClientToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div
        className="row-actions"
        style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 0 }} data-tour="page-title">
          Clients
        </h2>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)} data-tour="clients-create">
          New client
        </button>
      </div>
      <SearchForm
        data-tour="clients-search"
        onSubmit={(e) => {
          e.preventDefault();
          setLoading(true);
          void load();
        }}
      >
        <FormInput
          placeholder="Company, name, or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <button type="submit" className="btn-secondary">
          Search
        </button>
      </SearchForm>
      {loading ? <p className="muted">Loading…</p> : null}
      {notice ? <p className="success">{notice}</p> : null}
      {error ? <p className="err">{error}</p> : null}
      <div className="table-wrap" data-tour="clients-table">
        <table className="data-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Name</th>
              <th>Email</th>
              <th>Projects</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id}>
                <td>{c.company_name || '—'}</td>
                <td>{c.name}</td>
                <td>{c.email || '—'}</td>
                <td>{c.projects_count ?? '—'}</td>
                <td className="row-actions">
                  <Link to={`/admin/clients/${c.id}`} className="btn-link">
                    Open
                  </Link>
                  {c.email && !c.has_account ? (
                    <button
                      type="button"
                      className="btn-link"
                      disabled={invitingId === c.id}
                      onClick={() => void onInvite(c)}
                    >
                      {invitingId === c.id ? 'Inviting…' : 'Invite'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => setClientToDelete(c)}
                    data-tour="clients-delete"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showCreate} title="New client" onClose={closeCreate}>
        <form onSubmit={onCreate} className="stack">
          <label className="field">
            <span>Company</span>
            <input value={companyName} onChange={(ev) => setCompanyName(ev.target.value)} />
          </label>
          <label className="field">
            <span>Name</span>
            <input value={name} onChange={(ev) => setName(ev.target.value)} required />
          </label>
          <label className="field">
            <span>Email</span>
            <input type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} />
          </label>
          <label className="field">
            <span>Phone</span>
            <input value={phone} onChange={(ev) => setPhone(ev.target.value)} />
          </label>
          <label className="field">
            <span>Address</span>
            <textarea value={address} onChange={(ev) => setAddress(ev.target.value)} rows={2} />
          </label>
          <FieldErrors errors={fieldErrors} />
          <div className="row-actions" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn-secondary" onClick={closeCreate} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Create client'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(clientToDelete)}
        title={clientToDelete ? `Delete “${clientLabel(clientToDelete)}”?` : 'Delete client'}
        description={
          clientToDelete
            ? `This permanently removes the client and its ${clientToDelete.projects_count ?? 0} project(s). Portal users are unlinked, not deleted.`
            : undefined
        }
        onClose={() => {
          if (!deleting) {
            setClientToDelete(null);
          }
        }}
        footer={
          <ModalConfirmFooter
            danger
            confirmLabel="Delete"
            confirmBusy={deleting}
            onCancel={() => setClientToDelete(null)}
            onConfirm={() => void onDelete()}
          />
        }
      >
        <p className="muted small" style={{ margin: 0 }}>
          This action is permanent.
        </p>
      </Modal>
    </div>
  );
}
